import ZModal from "@atoms/ZModal";
import React, { useEffect, memo, useState, useContext } from "react";
import { MediaGuideUIProps, GuideMediaType } from "./interface";
import "./index.less";
import ZTypography from "@atoms/ZTypography";
import { EColors } from "@atoms/ZColors/interface";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import { WelcomeVideoStatus } from "../../contracts";
import ZVideo from "@organisms/ZVideo";
import uniqueId from "lodash/uniqueId";
import { ParentContext } from "../../store/context";

const { ZText } = ZTypography;

const MediaGuideModal: React.FC<MediaGuideUIProps> = ({
	fileType,
	fileName,
	buttonText,
	visible = false,
	handleNext,
	videoStatus,
	handleVideoTimeUpdate
}) => {
	const parentContext = useContext(ParentContext);
	const [initialVideoStatus, setInitialVideoStatus] = useState<
		WelcomeVideoStatus | undefined
	>(videoStatus);

	const onVideoPlay = () => {
		if (videoStatus && videoStatus !== WelcomeVideoStatus.WATCHED) {
			handleNext?.(WelcomeVideoStatus.WATCHED);
		}
	};

	useEffect(() => {
		if (videoStatus && !initialVideoStatus) {
			setInitialVideoStatus(videoStatus);
		}
	}, [videoStatus]);

	useEffect(() => {
		if (!!parentContext?.config.skipOnboarding) {
			handleNext?.(WelcomeVideoStatus.DONT_SHOW_AGAIN);
		} else if (videoStatus && videoStatus === WelcomeVideoStatus.UNOPENED) {
			handleNext?.(WelcomeVideoStatus.OPENED);
		}
	}, []);

	const mediaUrl = fileName ?? "";

	const handleSkip = () => {
		if (WelcomeVideoStatus.WATCHED === videoStatus) {
			handleNext?.(videoStatus);
		} else {
			handleNext?.(WelcomeVideoStatus.SKIPPED);
		}
	};

	const handleNextClick = () => {
		if (videoStatus) {
			if (
				[
					WelcomeVideoStatus.OPENED,
					WelcomeVideoStatus.UNOPENED
				].includes(videoStatus)
			) {
				handleNext?.(WelcomeVideoStatus.SKIPPED);
			} else if (WelcomeVideoStatus.SKIPPED === videoStatus) {
				handleNext?.(WelcomeVideoStatus.DONT_SHOW_AGAIN);
			} else {
				handleNext?.(videoStatus);
			}
		} else {
			handleNext?.();
		}
	};

	return (
		<ZModal
			visible={visible}
			closable={false}
			maskClosable={false}
			zIndex={920}
			footer={
				<div className="h-full flex items-center">
					{videoStatus && (
						<ZText
							color={EColors.GRAY}
							colorVariant={8}
							level={1}
							className="block cursor-pointer"
							onClick={handleSkip}
						>
							Skip For Now
						</ZText>
					)}
					<div
						className="flex items-center ml-auto cursor-pointer"
						onClick={handleNextClick}
					>
						<ZText color={EColors.GRAY} colorVariant={8} level={1}>
							{WelcomeVideoStatus.SKIPPED === initialVideoStatus
								? "Don't Show Again"
								: buttonText}
						</ZText>
						{WelcomeVideoStatus.SKIPPED !== initialVideoStatus && (
							<ZIcon
								name={EIcons.ARROWRIGHT}
								color={EColors.GRAY}
								colorVariant={8}
								size="sm"
								className="block ml-1"
							/>
						)}
					</div>
				</div>
			}
			className="guide-media-modal"
		>
			{fileType === GuideMediaType.VIDEO ? (
				<ZVideo
					id={uniqueId("onboarding-media-")}
					onPlay={onVideoPlay}
					video={mediaUrl}
					showPlayButton={true}
					autoPlay={!videoStatus}
					showPlayBtnOnPause={true}
					handleVideoTimeUpdate={handleVideoTimeUpdate}
				/>
			) : fileType === GuideMediaType.IMAGE ? (
				<div className="flex items-center justify-center">
					<img src={mediaUrl} />
				</div>
			) : null}
		</ZModal>
	);
};

export default memo(MediaGuideModal);
